<template>
  <ion-page>
    <ion-header>
      <ion-toolbar>
        <ion-title>
          <logo-img></logo-img>
        </ion-title>
        <ion-buttons slot="primary" v-if="!loadingCompanyInfo">
          <ion-button @click="openBrowser(companyInfo.websiteUrl)">
            <ion-icon slot="icon-only" :icon="compass"></ion-icon>
          </ion-button>
          <cart-button></cart-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content :fullscreen="true">
      <div class="ion-text-center ion-margin-top" v-if="loadingCompanyInfo">
        <ion-spinner></ion-spinner>
      </div>
      <ion-slides class="banner-slides" pager="true" :options="bannerSlideOpts" v-else>
        <ion-slide v-if="companyInfo.homeBanner1"><img class="banner-img" :src="companyInfo.banner1Link" /></ion-slide>
        <ion-slide v-if="companyInfo.homeBanner2"><img class="banner-img" :src="companyInfo.banner2Link" /></ion-slide>
        <ion-slide v-if="companyInfo.homeBanner3"><img class="banner-img" :src="companyInfo.banner3Link" /></ion-slide>
      </ion-slides>
      <ion-grid fixed>
        <ion-toolbar v-if="!loadingProductCategories" mode="md">
          <ion-segment @ionChange="categorySegmentChanged($event)" scrollable>
            <ion-segment-button class="product-category-btn" mode="md" v-for="category in allProductCategories" :key="category.id" :value="category.id">
              <ion-avatar>
                <img :src="category.imageLink">
              </ion-avatar>
              <ion-label>{{ category.title }}</ion-label>
            </ion-segment-button>
          </ion-segment>
        </ion-toolbar>
        <h1 class="section-title">{{ t('HomePage.featuredProducts') }}</h1>
        <div class="ion-text-center ion-margin-top" v-if="loadingProducts">
          <ion-spinner></ion-spinner>
        </div>
        <ion-slides pager="false" :options="tabSlideOps" v-else>
          <ion-slide v-for="product in featuredProducts" :key="product.id">
            <product-card :product="product" hideDetails="true"></product-card>
          </ion-slide>
        </ion-slides>
        <h1 class="section-title">{{ t('HomePage.latestProducts') }}</h1>
        <div class="ion-text-center ion-margin-top" v-if="loadingProducts">
          <ion-spinner></ion-spinner>
        </div>
        <ion-row v-else>
          <ion-col size-xs="6" size-md="4" size-xl="3" v-for="product in latestProducts" :key="product.id">
            <product-card :product="product" hideDetails="true"></product-card>
          </ion-col>
        </ion-row>

        <!-- Company Information -->
        <div class="ion-text-center ion-margin-top" v-if="loadingCompanyInfo">
          <ion-spinner></ion-spinner>
        </div>
        <ion-card v-else>
          <div class="ion-text-center ion-margin-top">
            <ion-icon class="icon-xl" :icon="informationCircle" color="warning"></ion-icon>
          </div>
          <ion-card-header class="ion-text-center" v-if="companyInfo.introductionEn || companyInfo.introduction">
            <ion-card-title><h1>{{ t('HomePage.aboutUs') }}</h1></ion-card-title>
          </ion-card-header>
          <ion-card-content>
            <div class="ion-text-center" style="white-space: pre-line; font-size: 16px" v-html="locale == 'zh' ? companyInfo.introduction : (companyInfo.introductionEn || companyInfo.introduction)"></div>
          </ion-card-content>

          <ion-card-header class="ion-text-center">
            <ion-card-title><h1>{{ t('HomePage.contactUs') }}</h1></ion-card-title>
          </ion-card-header>
          <ion-card-content class="ion-text-center contact-info">
            <h2 v-if="companyInfo.companyEmail"><ion-icon class="icon-md" :icon="mail"></ion-icon> {{ t('HomePage.companyEmail') }}<a :href="`mailto:${companyInfo.companyEmail}`">{{ companyInfo.companyEmail }}</a></h2>
            <h2 v-if="companyInfo.companyPhone"><ion-icon class="icon-md" :icon="call"></ion-icon> {{ t('HomePage.companyPhone') }}<a :href="`tel:${companyInfo.companyPhone}`">{{ companyInfo.companyPhone }}</a></h2>
            <h2 v-if="companyInfo.companyAddressEn || companyInfo.companyAddress"><ion-icon class="icon-md" :icon="location"></ion-icon> {{ t('HomePage.companyAddress') }}{{ locale == 'zh' ? companyInfo.companyAddress : (companyInfo.companyAddressEn || companyInfo.companyAddress) }}</h2>
          </ion-card-content>
          <!-- Embedded Google Maps -->
          <iframe :src="companyInfo.mapIframeSrc" width="100%" style="border:0;" allowfullscreen="" loading="lazy" v-if="companyInfo.mapIframeSrc"></iframe>
        </ion-card>
        <!-- Company Information -->

      </ion-grid>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { computed } from 'vue';

// icons
import { personCircle, compass, informationCircle, mail, call, location, } from 'ionicons/icons';

// components
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent,
        IonGrid, IonCol, IonRow, IonSpinner,
        IonCard, IonCardHeader, IonCardContent, IonCardTitle,
        IonSegment, IonSegmentButton, IonLabel, IonAvatar,
        IonButtons, IonButton, IonSlide, IonSlides, IonIcon, } from '@ionic/vue';
import ProductCard from "@/components/ProductCard.vue";
import CartButton from "@/components/CartButton.vue";
import LogoImg from "@/components/LogoImg.vue";

// services
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

import config from '@/config';
import { Plugins } from '@capacitor/core';

export default {
  name: 'HomePage',
  components: { IonPage, IonHeader, IonToolbar, IonTitle, IonContent,
                IonGrid, IonCol, IonRow, IonSpinner,
                IonCard, IonCardHeader, IonCardContent, IonCardTitle,
                IonSegment, IonSegmentButton, IonLabel, IonAvatar,
                IonButtons, IonButton, IonSlide, IonSlides, IonIcon,
                ProductCard, CartButton, LogoImg, },
  setup() {
    // methods
    const { t, locale } = useI18n();
    const store = useStore();
    const router = useRouter();
    
    const { Browser } = Plugins;

    // state variables
    const loadingProductCategories = computed(() => store.state.loadingProductCategories);
    const loadingProducts = computed(() => store.state.loadingProducts);
    //const banners = computed(() => store.state.homeBanners);
    const featuredProducts = computed(() => store.getters.featuredProducts);
    const latestProducts = computed(() => store.getters.latestProducts);
    const allProductCategories = computed(() => store.state.allProductCategories);
    const loadingCompanyInfo = computed(() => store.state.loadingCompanyInfo);
    const companyInfo = computed(() => store.state.companyInfo);
    const bannerSlideOpts = {
      slidesPerView: 1,
      autoHeight: true,
      initialSlide: 0,
      speed: config.HomePage.banner.slidingSpeed,
      loop: true,
      autoplay: {
        delay: config.HomePage.banner.autoPlayDelay,
      },
      breakpoints: {
        // when window width is >= 640px
        1024: {
          slidesPerView: config.HomePage.banner.desktopSlidesPerView,
        },
      },
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
    };
    const tabSlideOps = {
      slidesPerView: 2,
      freeMode: true,
      autoHeight: true,
      loop: true,
      autoplay: true,
      spaceBetween: 0,
      breakpoints: {
        // when window width is >= 640px
        768: {
          slidesPerView: 3,
        },
        1024: {
          slidesPerView: 4,
        }
      }
    };
    const categorySegmentChanged = (e: any) => {
      router.push({ name: 'ProductListPage', params: { category: e.target.value } }); // go to product page and pre-select category
    }
    const openBrowser = async (url: any) => {
      await Browser.open({ url, toolbarColor: config.primaryColor });
    }

    return {
      t,

      categorySegmentChanged, openBrowser,

      // icons
      personCircle, compass, informationCircle, mail, call, location,
      
      // variables
      loadingCompanyInfo, companyInfo,
      bannerSlideOpts, tabSlideOps,
      loadingProducts, loadingProductCategories,
      featuredProducts, latestProducts,
      allProductCategories,
      locale,
    }
  },
}
</script>

<style scoped>
  .section-title {
    margin-left: 5px;
  }
  .product-category-btn {
    --padding-top: 16px;
  }
  .banner-slides {
    max-height: 500px !important;
  }
  .banner-img {
    width: 100%;
    height: 400px;
    object-fit: cover;
  }
  .contact-info h2 {
    padding: 5px;
  }
  iframe {
    height: 480px;
  }
  @media screen and (max-width: 374px) {
    iframe {
      height: 280px;
    }
  }
</style>